import React, { useEffect, useState } from "react";
import { resolveMedia } from "@/lib/api";
import { ArrowCounterClockwise, Check, Crosshair, Lightning, PencilSimple, Trophy, X } from "@phosphor-icons/react";
import { ChampionBanner, LeaderBanner, ResultStatus, finalRows, tiebreakerRows, leadersOf, sortByResults } from "@/components/ShootoutResults";
import { ShootoutShareModal, ShareCardButton } from "@/components/ShareCardModal";

// League-wide championship round (top 8 across every division → one champion).
export function ShootoutFinal({ allCands, final, finalistsCount, busy, onScore, onReset, onStart, onCancel, onStartTiebreaker, onCancelTiebreaker }) {
  const [share, setShare] = useState(false);
  const done = allCands.filter((c) => c.completed).length;
  if (!final?.active) {
    if (done < 2) return null;
    return (
      <div className="flex items-center justify-between gap-3 flex-wrap border border-[#FFEA00]/30 bg-[#FFEA00]/[0.04] px-5 py-4 mb-6" data-testid="shootout-final-cta">
        <div className="flex items-center gap-3">
          <Trophy size={24} weight="fill" className="text-[#FFEA00]" />
          <div>
            <p className="font-display text-xl uppercase leading-none">Championship Round</p>
            <p className="text-white/50 text-xs mt-1">{done} preliminary score(s) in across all divisions — the top {Math.min(finalistsCount, done)} advance to shoot for one league champion.</p>
          </div>
        </div>
        <button onClick={onStart} disabled={busy} data-testid="shootout-start-final-btn"
          className="flex items-center gap-2 bg-[#FFEA00] hover:bg-[#FFF176] disabled:opacity-40 text-black font-bold uppercase tracking-wide px-4 py-2.5 text-xs transition-all duration-200">
          <Trophy size={14} weight="fill" /> Start Final Round (Top {Math.min(finalistsCount, done)})
        </button>
      </div>
    );
  }
  const rows = finalRows(allCands, final);
  return (
    <section className="border border-[#FFEA00]/40 bg-[#FFEA00]/[0.03] p-5 mb-8" data-testid="shootout-final-section">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-2">
          <Trophy size={22} weight="fill" className="text-[#FFEA00]" />
          <div>
            <h3 className="font-display text-2xl uppercase leading-none">Championship Round — All Divisions</h3>
            <p className="text-white/50 text-xs mt-1" data-testid="shootout-final-progress">Top {final.total} finalists · {final.completed_count} of {final.total} completed · highest final score is the league champion</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <ShareCardButton onClick={() => setShare(true)} testid="shootout-admin-share-btn" />
          <button onClick={onCancel} disabled={busy} data-testid="shootout-cancel-final-btn" className="flex items-center gap-1.5 border border-white/15 text-white/50 hover:text-[#FF3B30] hover:border-[#FF3B30]/50 font-bold uppercase tracking-wide px-3 py-2 text-xs transition-all duration-200">
            <X size={13} weight="bold" /> Cancel final round
          </button>
        </div>
      </div>
      <ChampionBanner final={final} testid="shootout-admin-champion" />
      {final.tied && (
        <div className="flex items-center justify-between gap-3 flex-wrap border border-[#FF3B30]/40 bg-[#FF3B30]/[0.06] px-4 py-3 mb-6" data-testid="shootout-tiebreaker-cta">
          <p className="text-sm text-white/70">
            <span className="font-bold text-white">{allCands.filter((c) => final.tied_ids.includes(c.player_id)).map((c) => c.name).join(" & ")}</span> are tied — start a sudden-death round (one more turn each, highest score wins). Repeats until decided.
          </p>
          <button onClick={onStartTiebreaker} disabled={busy} data-testid="shootout-start-tiebreaker-btn"
            className="flex items-center gap-2 bg-[#FF3B30] hover:bg-[#FF5C52] disabled:opacity-40 text-white font-bold uppercase tracking-wide px-4 py-2.5 text-xs transition-all duration-200">
            <Lightning size={14} weight="fill" /> Start Tiebreaker {final.tiebreaker ? `Round ${final.tiebreaker.round + 1}` : ""}
          </button>
        </div>
      )}
      {final.tiebreaker && (
        <section className="border border-[#FF3B30]/50 bg-[#FF3B30]/[0.05] p-4 mb-6" data-testid="shootout-admin-tiebreaker">
          <div className="flex items-center gap-3 mb-3 flex-wrap">
            <Lightning size={20} weight="fill" className="text-[#FF3B30]" />
            <h4 className="font-display text-xl uppercase leading-none">Sudden-Death Tiebreaker · Round {final.tiebreaker.round}</h4>
            <span className="text-white/40 text-xs" data-testid="shootout-admin-tiebreaker-progress">{final.tiebreaker.completed_count} of {final.tiebreaker.total} completed</span>
            <button onClick={onCancelTiebreaker} disabled={busy} data-testid="shootout-cancel-tiebreaker-btn" className="ml-auto flex items-center gap-1.5 border border-white/15 text-white/50 hover:text-[#FF3B30] hover:border-[#FF3B30]/50 font-bold uppercase tracking-wide px-3 py-1.5 text-[10px] transition-all duration-200">
              <X size={12} weight="bold" /> Cancel round {final.tiebreaker.round}
            </button>
          </div>
          <ScoreList rows={tiebreakerRows(allCands, final)} busy={busy} round="tiebreaker" onScore={onScore} onReset={onReset} />
        </section>
      )}
      {!final.champion && !final.tiebreaker && <LeaderBanner leaders={leadersOf(rows)} testid="shootout-admin-final-leader" />}
      <ScoreList rows={rows} busy={busy} round="final" onScore={onScore} onReset={onReset} />
      {share && <ShootoutShareModal champion={final.champion} onClose={() => setShare(false)} />}
    </section>
  );
}

export function ShootoutScoring({ cands, division, final, busy, onScore, onReset, onClearAll }) {
  const done = cands.filter((c) => c.completed).length;

  if (!cands.length) {
    return (
      <div className="border-2 border-dashed border-white/15 py-16 text-center" data-testid="shootout-scoring-empty">
        <Crosshair size={40} className="text-white/25 mx-auto mb-4" />
        <p className="text-white/50">No {division} division candidates to score yet.</p>
      </div>
    );
  }

  return (
    <section data-testid="shootout-scoring">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div>
          <h3 className="font-display text-2xl uppercase">{final?.active ? `${division} — Preliminary Round` : `${division} — Live Scoring`}</h3>
          <p className="text-white/50 text-sm" data-testid="shootout-scoring-progress">
            <span className="text-white font-bold">{done}</span> of <span className="text-white font-bold">{cands.length}</span> players completed — scores publish to the public page instantly.
          </p>
        </div>
        {done > 0 && (
          <button onClick={onClearAll} disabled={busy} data-testid="shootout-clear-results-btn" className="flex items-center gap-1.5 border border-white/15 text-white/60 hover:text-[#FF3B30] hover:border-[#FF3B30]/50 font-bold uppercase tracking-wide px-3 py-2 text-xs transition-all duration-200">
            <ArrowCounterClockwise size={13} /> Clear {division} scores
          </button>
        )}
      </div>
      {!final?.active && <LeaderBanner leaders={leadersOf(cands)} testid="shootout-admin-leader" />}
      <ScoreList rows={cands} busy={busy} round="prelim" onScore={onScore} onReset={onReset} highlightIds={final?.active ? new Set(final.finalist_ids) : null} />
    </section>
  );
}

function ScoreList({ rows, busy, round, onScore, onReset, highlightIds }) {
  const sorted = sortByResults(rows);
  const leaders = leadersOf(rows);
  return (
    <div className="border border-white/10 bg-[#121212] divide-y divide-white/5" data-testid={`shootout-score-list-${round}`}>
      {sorted.map((c) => (
        <ScoreRow key={c.player_id} c={c} busy={busy} round={round} leader={leaders.some((l) => l.player_id === c.player_id)}
          finalist={highlightIds ? highlightIds.has(c.player_id) : false} onScore={onScore} onReset={onReset} />
      ))}
    </div>
  );
}

function ScoreRow({ c, busy, round, leader, finalist, onScore, onReset }) {
  const saved = c.completed ? String(c.points) : "";
  const [val, setVal] = useState(saved);
  useEffect(() => { setVal(saved); }, [saved]);
  const dirty = val !== saved;
  const valid = val !== "" && Number.isInteger(Number(val)) && Number(val) >= 0 && Number(val) <= 999;
  const tid = round === "prelim" ? c.player_id : `${round}-${c.player_id}`;

  const submit = () => { if (valid) onScore(c.player_id, Number(val), round); };

  return (
    <div className={`px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3 ${leader ? "bg-[#FFEA00]/5" : ""}`} data-testid={`shootout-score-row-${tid}`}>
      <div className="w-8 font-display text-xl tabular-nums text-center shrink-0 hidden sm:block">
        {c.place ? <span className={leader ? "text-[#FFEA00]" : "text-white/60"}>{c.place}</span> : <span className="text-white/20">—</span>}
      </div>
      <div className="flex items-center gap-3 flex-1 min-w-0">
        {c.photo_url
          ? <img src={resolveMedia(c.photo_url)} alt="" className="w-10 h-10 object-cover shrink-0" />
          : <span className="w-10 h-10 flex items-center justify-center font-display text-lg shrink-0" style={{ backgroundColor: c.team_color || "#FF5722", color: "#000" }}>{(c.name || "?").charAt(0)}</span>}
        <div className="min-w-0">
          <p className="font-display text-lg uppercase truncate leading-tight flex items-center gap-2">
            {c.name} {c.number != null && c.number !== "" && <span className="text-white/40 text-sm">#{c.number}</span>}
            {finalist && <span className="text-[9px] font-sans font-bold tracking-wider bg-[#FFEA00]/15 text-[#FFEA00] border border-[#FFEA00]/40 px-1.5 py-0.5" data-testid={`shootout-finalist-tag-${c.player_id}`}>Finalist</span>}
          </p>
          <p className="text-white/50 text-[11px] uppercase tracking-wide truncate">
            {c.team_name}{round === "final" && <span className="text-white/35"> · {c.division} · Prelim {c.prelim_points ?? "—"} pts</span>}{round === "tiebreaker" && <span className="text-white/35"> · Final {c.final_pts ?? "—"} pts</span>}
          </p>
        </div>
      </div>
      <div className="shrink-0"><ResultStatus c={c} /></div>
      <div className="flex items-center gap-2 shrink-0">
        <input
          type="number" min="0" max="999" inputMode="numeric" value={val} disabled={busy}
          onChange={(e) => setVal(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()}
          placeholder="Pts" data-testid={`shootout-points-input-${tid}`}
          className={`w-20 bg-[#1A1A1A] border px-2.5 py-2 text-center font-display text-2xl tabular-nums focus:outline-none focus:ring-1 focus:ring-[#FF5722] ${dirty ? "border-[#FF5722]" : "border-white/10"}`}
        />
        <button onClick={submit} disabled={busy || !dirty || !valid} data-testid={`shootout-save-score-${tid}`}
          title={c.completed ? "Update score" : "Save score & mark completed"}
          className="flex items-center gap-1.5 bg-[#FF5722] hover:bg-[#FF7043] disabled:opacity-30 text-black font-bold uppercase tracking-wide px-3 py-2.5 text-xs transition-all duration-200">
          {c.completed ? <><PencilSimple size={13} weight="bold" /> Update</> : <><Check size={13} weight="bold" /> Complete</>}
        </button>
        <button onClick={() => onReset(c.player_id, c.name, round)} disabled={busy || !c.completed} data-testid={`shootout-reset-score-${tid}`}
          title="Reset score" className="text-white/30 hover:text-[#FF3B30] disabled:opacity-20 p-1.5 transition-colors">
          <ArrowCounterClockwise size={17} />
        </button>
      </div>
    </div>
  );
}
